<template>
  <f7-card class="padding-vertical-half padding-right tarjeta-funciones" v-if="username">
    <f7-card-header>{{ $t('empleadofunciones.titulo') }}</f7-card-header>
    <f7-card-content>
      <f7-block v-if="error403" strong class="no-margin">
        <p>{{ $t('empleadofunciones.noAutorizado') }}</p>
      </f7-block>
      <!-- Sin ficha no hay tabla ni botones, así que un fallo pasajero de la primera carga
           dejaría la tarjeta inservible hasta salir y volver a abrir la ficha del empleado: no
           quedaría ninguna acción con la que repetir la petición (mismo criterio que la tarjeta
           de domicilio, revisión CODEX del PR #1709). -->
      <f7-block v-else-if="cargaFallida" strong class="no-margin carga-fallida">
        <p>
          {{ $t('empleadofunciones.sinCargar') }}
          <f7-link class="margin-left-half" @click="cargar()">{{ $t('empleadofunciones.reintentar') }}</f7-link>
        </p>
      </f7-block>

      <template v-else-if="ficha">
        <!-- Las ~15 personas con dos cuentas vinculadas (#52) tienen unas solas funciones, y se
             gestionan en UNA sola de sus cuentas: la que tiene el check «Gestiona vacaciones en
             este usuario». Desde las demás la tarjeta se mira pero no se toca, y aquí se dice
             dónde ir a editarlas: sin eso, lo único que se vería es que no hay botones.
             Esto es presentación; la garantía está en el servidor, que rechaza igual una petición
             directa a la API desde una cuenta que no es la gestora. -->
        <f7-block v-if="ficha.motivoSoloLectura" strong class="sugerencia aviso no-margin-vertical">
          <b>{{ $t('empleadofunciones.soloLectura') }}.</b> {{ ficha.motivoSoloLectura }}
        </f7-block>
        <f7-block v-else-if="otraCuenta" strong class="sugerencia no-margin-vertical">
          {{ $t('empleadofunciones.cuentaCompartida') }} <b>{{ ficha.cuentaFunciones }}</b>
        </f7-block>

        <!-- ------------------------------------------------ funciones vigentes -->
        <f7-block-title class="titulo-bloque">{{ $t('empleadofunciones.ahora') }}</f7-block-title>
        <div class="tabla-wrapper">
          <table class="tabla-funciones">
            <thead>
              <tr>
                <th>{{ $t('empleadofunciones.codigo') }}</th>
                <th>{{ $t('empleadofunciones.funcion') }}</th>
                <th>{{ $t('empleadofunciones.puesto') }}</th>
                <th>{{ $t('empleadofunciones.desde') }}</th>
                <th>{{ $t('empleadofunciones.principal') }}</th>
                <th v-if="editable"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="fila in ficha.vigentes" :key="fila.id" :class="{ huerfana: fila.huerfana }">
                <!-- El código que se LEE, no el interno con el que el tramo la nombra: desde la
                     #483 son dos cosas distintas y el que reconoce quien mira es el visible. -->
                <td><span class="codigo">{{ fila.funcionCodigo || fila.codigoFuncion }}</span></td>
                <td>
                  {{ fila.funcionNombre || fila.nombreFuncionAlAlta }}
                  <!-- Una función guardada en una cuenta que no es la gestora no cuenta en los
                       listados de Ajustes ni en las reglas por funciones: enseñarla sin más sería
                       presentarla como válida, que es justo la diferencia entre lo que la ficha
                       dice y lo que los contadores cuentan. -->
                  <span
                    v-if="fila.huerfana"
                    class="marca-huerfana"
                    :title="$t('empleadofunciones.huerfanaAviso')"
                  >⚠ {{ $t('empleadofunciones.huerfana') }}{{ fila.username ? ' · ' + fila.username : '' }}</span>
                </td>
                <td>{{ fila.puestoNombre || '—' }}</td>
                <td>{{ fecha(fila.desde) }}</td>
                <td>
                  <span v-if="fila.principal" class="marca-principal">★ {{ $t('empleadofunciones.principal') }}</span>
                </td>
                <td v-if="editable" class="col-acciones">
                  <f7-button small @click="abrirFinalizar(fila)">{{ $t('empleadofunciones.finalizar') }}</f7-button>
                </td>
              </tr>
              <tr v-if="!ficha.vigentes.length">
                <td :colspan="editable ? 6 : 5" class="vacio">{{ $t('empleadofunciones.sinVigentes') }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- ------------------------------------------------ añadir función -->
        <template v-if="editable">
          <f7-block-title class="titulo-bloque">{{ $t('empleadofunciones.anadir') }}</f7-block-title>
          <!-- «Desde» se propone a hoy, y «hoy» se vuelve a calcular en cuanto alguien entra en
               el bloque a dar de alta algo: una ficha que se quedó abierta de ayer seguía
               proponiendo ayer (revisión CODEX del PR #1732). Al entrar todavía no hay nada
               escrito, así que no se pisa nada. -->
          <div class="bloque-alta" @focusin="alEmpezarAlta">
            <r-form ref="formAlta" :key="'alta-' + claveAlta" v-slot="{ rform }" :initialData="altaEnBlanco">
            <f7-row no-gap class="list list-form no-hairlines">
              <f7-col width="100" medium="25">
                <ul>
                  <!-- Solo funciones ACTIVAS: las trae ya filtradas el backend, que es quien
                       decide qué se puede asignar. -->
                  <r-input
                    outline
                    floating-label
                    :label="$t('empleadofunciones.funcion')"
                    :form="rform"
                    type="select"
                    name="codigoFuncion"
                  >
                    <option :value="null">{{ $t('empleadofunciones.eligeFuncion') }}</option>
                    <!-- El valor es el código INTERNO y lo que se enseña es el visible (#483):
                         el tramo se guarda contra el interno, que no cambia aunque después se
                         reordenen los códigos del catálogo. -->
                    <option v-for="f in ficha.funcionesDisponibles" :key="f.id" :value="f.codigoInterno">
                      {{ f.codigo }} · {{ f.nombre }}{{ f.puestoNombre ? ' (' + f.puestoNombre + ')' : '' }}
                    </option>
                  </r-input>
                </ul>
              </f7-col>
              <f7-col width="100" medium="20">
                <ul>
                  <r-input
                    outline
                    floating-label
                    :label="$t('empleadofunciones.desde')"
                    :form="rform"
                    type="datepicker"
                    name="desde"
                    :calendar-params="calendario"
                  ></r-input>
                </ul>
              </f7-col>
              <f7-col width="100" medium="25">
                <ul>
                  <r-input
                    outline
                    floating-label
                    :label="$t('empleadofunciones.motivoObligatorio')"
                    :form="rform"
                    type="text"
                    name="motivo"
                  ></r-input>
                </ul>
              </f7-col>
              <f7-col width="100" medium="10">
                <ul>
                  <r-input
                    :label="$t('empleadofunciones.funcionPrincipal')"
                    :form="rform"
                    type="check"
                    name="principal"
                  ></r-input>
                </ul>
              </f7-col>
              <!-- El alta de una en una no cambia: «Añadir funciones de un puesto…» es una opción
                   MÁS, al lado, y abre su ventana sin tocar nada de lo que hay escrito aquí. Las
                   dos comparten fila porque es donde dirección las puso (maqueta de la #485);
                   F7 da ancho completo a todo botón, así que van en un contenedor flex. -->
              <f7-col width="100" medium="20" class="display-flex align-items-center botones-alta">
                <f7-button fill icon-f7="plus" :text="$t('empleadofunciones.boton')" class="display-flex" @click="anadir"></f7-button>
                <f7-button outline :text="$t('empleadofunciones.desdePuesto')" class="display-flex boton-puesto" @click="abrirPuesto"></f7-button>
              </f7-col>
            </f7-row>
            </r-form>
            <!-- `r-input` no tiene marcador, así que el ejemplo del motivo va debajo: sin él,
                 «Motivo» se rellena con un «cambio» que no dice nada, y es lo que va a leer
                 dentro de cinco años quien audite el organigrama (I6). -->
            <p class="pie">{{ $t('empleadofunciones.motivoEjemplo') }}</p>
            <!-- Sin catálogo no se puede dar de alta nada: se dice y se manda a Ajustes, en vez
                 de dejar un desplegable vacío sin explicación. -->
            <p v-if="!ficha.funcionesDisponibles.length" class="pie aviso">{{ $t('empleadofunciones.sinFunciones') }}</p>
          </div>
        </template>

        <!-- ------------------------------------------------ histórico -->
        <f7-block-title class="titulo-bloque">{{ $t('empleadofunciones.historico') }}</f7-block-title>
        <div class="tabla-wrapper">
          <table class="tabla-funciones">
            <thead>
              <tr>
                <th>{{ $t('empleadofunciones.funcion') }}</th>
                <th>{{ $t('empleadofunciones.puesto') }}</th>
                <th>{{ $t('empleadofunciones.desde') }}</th>
                <th>{{ $t('empleadofunciones.hasta') }}</th>
                <th>{{ $t('empleadofunciones.motivo') }}</th>
                <th>{{ $t('empleadofunciones.registradoPor') }}</th>
              </tr>
            </thead>
            <tbody>
              <!-- El histórico enseña la FOTO DEL ALTA, no la lectura de hoy: si una función se
                   recoloca o se renombra, el pasado tiene que seguir diciendo lo que había
                   entonces (JJCS, 22/09/2026; trazabilidad 17020). -->
              <tr v-for="fila in ficha.historico" :key="fila.id" :class="{ finalizada: !fila.vigente }">
                <td>
                  {{ fila.nombreFuncionAlAlta || fila.funcionNombre }}
                  <span v-if="fila.principal" class="marca-principal" :title="$t('empleadofunciones.principal')">★</span>
                </td>
                <td>{{ fila.nombrePuestoAlAlta || '—' }}</td>
                <td>{{ fecha(fila.desde) }}</td>
                <td>
                  <span v-if="fila.vigente" class="etiqueta-vigente">{{ $t('empleadofunciones.vigente') }}</span>
                  <span v-else>{{ fecha(fila.hasta) }}</span>
                </td>
                <!-- Los DOS motivos, no sólo el del alta: el de la baja es obligatorio al
                     finalizar y este histórico es el único sitio donde se puede leer. Sin él,
                     quien audite el organigrama ve por qué se asignó la función pero no por qué
                     terminó (I6; revisión CODEX del PR #1732). -->
                <td class="col-motivo">
                  <template v-if="!fila.vigente && fila.motivoBaja">
                    <span class="motivo-linea">
                      <b>{{ $t('empleadofunciones.motivoAltaEtiqueta') }}</b> {{ fila.motivoAlta || '—' }}
                    </span>
                    <span class="motivo-linea">
                      <b>{{ $t('empleadofunciones.motivoBajaEtiqueta') }}</b> {{ fila.motivoBaja }}
                    </span>
                  </template>
                  <template v-else>{{ fila.motivoAlta || '—' }}</template>
                </td>
                <td>{{ fila.registradoPor || '—' }}</td>
              </tr>
              <tr v-if="!ficha.historico.length">
                <td colspan="6" class="vacio">{{ $t('empleadofunciones.sinHistorico') }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p class="pie">{{ $t('empleadofunciones.pie') }}</p>
      </template>
    </f7-card-content>

    <!-- «Finalizar» pide fecha y motivo, los dos obligatorios: en un popup y no en la fila, para
         que no se pueda cerrar una función de un clic sin escribir por qué. -->
    <f7-popup :opened="!!finalizando" @popup:closed="finalizando = null" class="popup-finalizar">
      <f7-page v-if="finalizando">
        <f7-navbar :title="$t('empleadofunciones.finalizarTitulo')">
          <f7-nav-right>
            <f7-link popup-close>{{ $t('empleadofunciones.cancelar') }}</f7-link>
          </f7-nav-right>
        </f7-navbar>
        <f7-block strong class="no-margin-vertical">
          <p class="que-se-finaliza">
            <span class="codigo">{{ finalizando.funcionCodigo || finalizando.codigoFuncion }}</span>
            {{ finalizando.funcionNombre || finalizando.nombreFuncionAlAlta }}
            · {{ $t('empleadofunciones.desde') }} {{ fecha(finalizando.desde) }}
          </p>
        </f7-block>
        <r-form ref="formFin" :key="'fin-' + finalizando.id" v-slot="{ rform }" :initialData="finVacio">
        <f7-row no-gap class="list list-form no-hairlines">
          <f7-col width="100" medium="50">
            <ul>
              <r-input
                outline
                floating-label
                :label="$t('empleadofunciones.hastaObligatorio')"
                :form="rform"
                type="datepicker"
                name="hasta"
                :calendar-params="calendario"
              ></r-input>
            </ul>
          </f7-col>
          <f7-col width="100" medium="50">
            <ul>
              <r-input
                outline
                floating-label
                :label="$t('empleadofunciones.motivoObligatorio')"
                :form="rform"
                type="text"
                name="motivo"
              ></r-input>
            </ul>
          </f7-col>
        </f7-row>
        </r-form>
        <f7-block>
          <f7-button fill @click="finalizar">{{ $t('empleadofunciones.finalizar') }}</f7-button>
        </f7-block>
      </f7-page>
    </f7-popup>

    <!-- «Añadir funciones de un puesto…» (#485): se elige el puesto, se rellenan «Desde» y
         «Motivo» una sola vez y se repasa la lista antes de incorporar nada. Las que ya desempeña
         salen igualmente, en gris y sin poder marcarse: esconderlas dejaría sin explicar por qué
         un puesto de 30 funciones ofrece 27. -->
    <f7-popup :opened="popupPuesto" @popup:closed="cerrarPuesto" class="popup-funciones-puesto">
      <f7-page>
        <f7-navbar :title="$t('empleadofunciones.desdePuestoTitulo')">
          <f7-nav-right>
            <f7-link popup-close>{{ $t('empleadofunciones.cancelar') }}</f7-link>
          </f7-nav-right>
        </f7-navbar>

        <r-form ref="formLote" :key="'lote-' + claveLote" v-slot="{ rform }" :initialData="loteEnBlanco">
        <f7-row no-gap class="list list-form no-hairlines">
          <f7-col width="100" medium="34">
            <ul>
              <r-input
                outline
                floating-label
                :label="$t('empleadofunciones.puestoObligatorio')"
                :form="rform"
                type="select"
                name="puestoId"
                @change="alElegirPuesto"
              >
                <option :value="null">{{ $t('empleadofunciones.eligePuesto') }}</option>
                <option v-for="p in puestos" :key="p.id" :value="p.id">
                  {{ p.nombre }}{{ p.codigo ? ' (' + p.codigo + ')' : '' }}
                </option>
              </r-input>
            </ul>
          </f7-col>
          <f7-col width="100" medium="22">
            <ul>
              <r-input
                outline
                floating-label
                :label="$t('empleadofunciones.desdeObligatorio')"
                :form="rform"
                type="datepicker"
                name="desde"
                :calendar-params="calendario"
                @change="reflejarLote"
              ></r-input>
            </ul>
          </f7-col>
          <f7-col width="100" medium="44">
            <ul>
              <!-- Un solo motivo para todas, y obligatorio igual que en el alta de una en una. Se
                   refleja en `motivoLote` al teclear porque de él depende si el botón de confirmar
                   se puede pulsar, y eso se tiene que ver sin salir del campo. -->
              <r-input
                outline
                floating-label
                :label="$t('empleadofunciones.motivoTodas')"
                :form="rform"
                type="text"
                name="motivo"
                @change="reflejarLote"
              ></r-input>
            </ul>
          </f7-col>
        </f7-row>
        </r-form>

        <template v-if="funcionesPuesto">
          <div class="lista-puesto">
            <div class="cabecera-lista">
              <label class="marcar-todas">
                <input
                  type="checkbox"
                  ref="checkTodas"
                  :checked="todasMarcadas"
                  :disabled="!marcables.length"
                  @change="alternarTodas"
                />
                <span>{{ $t('empleadofunciones.marcarTodas') }}</span>
              </label>
              <span v-if="yaVigentes.length" class="cuantas-ya">
                · {{ $t('empleadofunciones.yaDesempenaVarias', { n: yaVigentes.length }) }}
              </span>
              <span class="contador">
                {{ $t('empleadofunciones.marcadasDe', { marcadas: marcadasCount, total: marcables.length }) }}
              </span>
            </div>

            <div
              v-for="f in funcionesPuesto.funciones"
              :key="f.codigoInterno"
              class="fila-funcion"
              :class="{ bloqueada: f.yaVigente }"
            >
              <input
                type="checkbox"
                :disabled="f.yaVigente"
                :checked="!!marcadas[f.codigoInterno]"
                @change="alternar(f)"
              />
              <span class="codigo">{{ f.codigo }}</span>
              <span class="nombre">{{ f.nombre }}</span>
              <!-- Junto a la propia fila, y con la fecha: es lo que dice por qué esa casilla no se
                   puede marcar. -->
              <span v-if="f.yaVigente" class="ya-desempena">
                {{ $t('empleadofunciones.yaDesempenaDesde', { fecha: fecha(f.desdeVigente) }) }}
              </span>
            </div>

            <p v-if="!funcionesPuesto.funciones.length" class="vacio-lista">
              {{ $t('empleadofunciones.puestoSinFunciones') }}
            </p>
          </div>
        </template>

        <f7-block class="pie-ventana">
          <p class="pie">{{ $t('empleadofunciones.pieVentana') }}</p>
          <!-- El botón deshabilitado NUNCA se queda mudo: el motivo va aquí, pegado a él, y no en
               un aviso al principio de la ventana (§9 del CLAUDE.md del orquestador). -->
          <div class="acciones-ventana">
            <span v-if="motivoNoConfirmar" class="motivo-bloqueo">{{ motivoNoConfirmar }}</span>
            <f7-button class="boton-cancelar" @click="cerrarPuesto">{{ $t('empleadofunciones.cancelar') }}</f7-button>
            <f7-button fill :disabled="!!motivoNoConfirmar" @click="confirmarPuesto">{{ textoConfirmar }}</f7-button>
          </div>
        </f7-block>
      </f7-page>
    </f7-popup>
  </f7-card>
</template>
<style scoped>
.tarjeta-funciones .card-header { font-weight: bold; }
.titulo-bloque { text-transform: uppercase; font-size: 12px; color: #8e8e93; margin-bottom: 4px; }
.tabla-wrapper { overflow-x: auto; margin: 0 16px; }
.tabla-funciones { width: 100%; border-collapse: collapse; font-size: 13px; }
.tabla-funciones th,
.tabla-funciones td {
  border-bottom: 1px solid #e0e0e0;
  padding: 4px 6px;
  text-align: left;
  vertical-align: top;
}
.tabla-funciones th { background-color: #f4f5f7; font-weight: bold; }
.tabla-funciones .vacio { color: #8e8e93; font-style: italic; }
.tabla-funciones .finalizada { color: #6d6d72; }
.tabla-funciones .col-motivo { max-width: 280px; word-break: break-word; }
.tabla-funciones .motivo-linea { display: block; }
.tabla-funciones .col-acciones { white-space: nowrap; }
.codigo {
  font-family: monospace;
  background-color: #f0f0f3;
  border-radius: 3px;
  padding: 1px 4px;
}
.marca-principal { font-weight: bold; white-space: nowrap; }
.marca-huerfana { white-space: nowrap; color: #b26a00; font-size: 11px; margin-left: 4px; }
.tabla-funciones .huerfana { background-color: #fff8e1; }
.etiqueta-vigente {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 11px;
  border: 1px solid #2196f3;
  color: #2196f3;
}
.bloque-alta { margin: 0 16px; border: 1px solid #e0e0e0; border-radius: 4px; padding: 4px 8px; }
.pie { font-size: 12px; color: #8e8e93; margin: 8px 16px; }
.pie.aviso { color: #b26a00; }
.carga-fallida { color: #8e8e93; font-size: 13px; }
.sugerencia { font-size: 13px; }
.sugerencia.aviso { color: #b26a00; }
.que-se-finaliza { font-size: 13px; margin: 0; }

/* ---------------------------------------- «Añadir funciones de un puesto…» (#485) */
/* Framework7 da ancho completo a todo botón, así que los dos de la fila van en un flex y cada
   uno ocupa lo suyo: sin esto el segundo se comía la fila entera. */
.botones-alta { gap: 8px; }
.botones-alta .button { flex: 0 1 auto; white-space: nowrap; }
.botones-alta .boton-puesto { font-size: 13px; }
/* El `:focus` a secas es el que funciona en todas partes; el par siguiente lo retira al pulsar con
   el ratón donde el navegador sabe distinguirlo. Un navegador que no entienda `:focus-visible`
   descarta las dos reglas y se queda con el recuadro siempre, que es el fallo del lado seguro
   (mismo patrón que `novedades.vue`). */
.boton-puesto:focus { outline: 2px solid #22355b; outline-offset: 2px; }
.boton-puesto:focus:not(:focus-visible) { outline: none; }
.boton-puesto:focus-visible { outline: 2px solid #22355b; outline-offset: 2px; }

.lista-puesto { margin: 0 16px; border: 1px solid #e0e0e0; border-radius: 4px; }
.cabecera-lista {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  background-color: #f4f5f7;
  border-bottom: 1px solid #e0e0e0;
  font-size: 13px;
}
.cabecera-lista .marcar-todas { display: flex; align-items: center; gap: 6px; font-weight: bold; }
.cabecera-lista .cuantas-ya { color: #8e8e93; font-size: 12px; }
/* El contador a la derecha, que es donde lo puso la maqueta: se lee sin buscarlo. */
.cabecera-lista .contador { margin-left: auto; color: #6d6d72; white-space: nowrap; }
.fila-funcion {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 4px 8px;
  border-bottom: 1px solid #f0f0f3;
  font-size: 13px;
}
.fila-funcion:last-child { border-bottom: none; }
.fila-funcion .nombre { flex: 1 1 auto; }
.fila-funcion.bloqueada { color: #8e8e93; background-color: #fafafa; }
.fila-funcion .ya-desempena {
  color: #b26a00;
  font-size: 12px;
  font-style: italic;
  white-space: nowrap;
}
.vacio-lista { color: #8e8e93; font-style: italic; font-size: 13px; margin: 8px; }

.pie-ventana .pie { margin-left: 0; margin-right: 0; }
.acciones-ventana { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.acciones-ventana .button { flex: 0 1 auto; white-space: nowrap; }
/* El motivo por el que no se puede confirmar, pegado al botón y no en la cabecera: quien pulsa no
   relaciona lo uno con lo otro (§9). Se lleva el hueco sobrante para quedarse a su izquierda. */
.acciones-ventana .motivo-bloqueo {
  flex: 1 1 240px;
  color: #b26a00;
  font-size: 12px;
  background-color: #fff8e1;
  border-radius: 4px;
  padding: 4px 8px;
}
</style>
<script>
import rForm from "./../../components/rForm.vue";
import rInput from "./../../components/rInput.vue";
import RhEmpleadoFunciones from "./../../js/db/RhEmpleadoFunciones.js";
import carrilPeticiones from "./../../js/utils/carrilPeticiones.js";
import preloaderDeLaSesion from "./../../js/utils/preloaderDeLaSesion.js";
import cerrojoDeLaSesion from "./../../js/utils/cerrojoDeLaSesion.js";

/**
 * El bloque «Añadir función» en blanco, con «Desde» a hoy: el alta más habitual es la de ahora
 * mismo. Se llama cada vez que se rehace el formulario, no una sola al montar la tarjeta.
 *
 * La fecha va SUELTA, no dentro de un array: `rInput.getValue()` envuelve él solo el valor de un
 * `datepicker` para pasárselo a Framework7, y su `setValue` guarda después `val[0]` —una fecha
 * suelta— en el formulario. Con el array puesto a mano, el calendario recibía `[[Date]]` y
 * `setValue` reventaba al elegir un día leyendo `previousVal[0].getHours()` sobre un array
 * (revisión CODEX del PR #1732).
 */
function enBlancoAlta() {
  return { codigoFuncion: null, desde: new Date(), principal: false, motivo: null };
}

/**
 * La ventana «Añadir funciones de un puesto…» en blanco (#485), con «Desde» a hoy por el mismo
 * motivo que el bloque de alta: lo normal es incorporarlas desde hoy. Se genera al abrir la
 * ventana, no al montar la tarjeta, para que una ficha que se quedó abierta de ayer no proponga
 * ayer.
 *
 * La fecha va SUELTA y no dentro de un array: ver la nota de `enBlancoAlta()`.
 */
function enBlancoLote() {
  return { puestoId: null, desde: new Date(), motivo: null };
}

export default {
  name: "rh-funciones-card",
  components: {
    rForm,
    rInput
  },
  props: {
    username: {
      type: String
    },
    // El modo de la ficha que la contiene. La tarjeta escribe por su cuenta, así que sin esto la
    // vista de solo lectura del empleado dejaría añadir y finalizar funciones.
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data: function() {
    return {
      ficha: null,
      // Lo que dice el servidor sobre si se puede escribir AQUÍ: el permiso de ADMIN/DIRECTOR ya
      // cruzado con que esta sea la cuenta gestora de la persona (#51). Para saber si se puede,
      // `editable`, que le añade el modo de la ficha.
      permisoEdicion: false,
      error403: false,
      // La carga falló por algo que no es un 403: no hay tarjeta que pintar, pero sí desde dónde
      // volver a intentarlo.
      cargaFallida: false,
      // Sube al cambiar para rehacer el r-form del alta: es como se vacía el bloque después de
      // añadir, mismo recurso que el `formKey` de Ajustes · Funciones.
      claveAlta: 0,
      // Los datos iniciales de los dos formularios. NO son computed: uno sin dependencias
      // reactivas se evalúa una vez y Vue 2 lo deja almacenado, así que `new Date()` se quedaba
      // con el día en que se montó la tarjeta y una ficha abierta de un día para otro seguía
      // proponiendo ayer (revisión CODEX del PR #1732). Se generan al crear cada formulario:
      // `rehacerAlta()` el del alta y `abrirFinalizar()` el del popup.
      altaEnBlanco: enBlancoAlta(),
      finVacio: null,
      // El tramo que se está finalizando, o null. Es lo que abre el popup.
      finalizando: null,
      // --- Ventana «Añadir funciones de un puesto…» (#485) ---
      popupPuesto: false,
      // Sube al abrir la ventana para rehacer su r-form, igual que `claveAlta` con el bloque de
      // alta: es como se vacía sin arrastrar lo de la vez anterior.
      claveLote: 0,
      loteEnBlanco: enBlancoLote(),
      // Lo que ha contestado el servidor para el puesto elegido, o null mientras no haya ninguno.
      funcionesPuesto: null,
      cargandoPuesto: false,
      // Por qué no se ha podido cargar la lista. Es uno de los motivos por los que el botón de
      // confirmar queda deshabilitado, y como tal se enseña a su lado.
      errorPuesto: null,
      // Qué funciones están marcadas, por código. Las que ya desempeña nunca entran aquí.
      marcadas: {},
      // El motivo y el «Desde» tal y como se van rellenando. Viven aquí además de en el
      // formulario porque de ellos depende si el botón de confirmar se puede pulsar, y un computed
      // que leyera `$refs` no se volvería a evaluar al escribir. Los escribe `reflejarLote`,
      // leyéndolos SIEMPRE del formulario.
      motivoLote: "",
      // En aaaa-mm-dd, que es lo que se envía: así el gate mide exactamente lo que se manda.
      desdeLote: null,
      calendario: {
        closeOnSelect: true,
        routableModals: false,
        dateFormat: { year: "numeric", month: "2-digit", day: "2-digit" }
      }
    };
  },
  computed: {
    // Hacen falta las dos: el permiso del servidor manda sobre quién puede tocar las funciones, y
    // el modo de la ficha sobre si esta pantalla se está editando o solo se está mirando.
    editable: function() {
      return this.permisoEdicion && !this.readonly;
    },
    // Si las funciones de esta persona se gestionan en su OTRA cuenta de la ERP. Mayúsculas
    // aparte: el username llega de la ruta y la ficha lo devuelve como está en la ERP. Mismo
    // criterio que `otraCuenta` de la tarjeta de domicilio.
    otraCuenta: function() {
      const cuenta = this.ficha ? this.ficha.cuentaFunciones : null;
      return !!cuenta && !!this.username && cuenta.toUpperCase() !== this.username.toUpperCase();
    },

    // --- Ventana «Añadir funciones de un puesto…» (#485) ---

    puestos: function() {
      return this.ficha ? this.ficha.puestosDisponibles : [];
    },
    // Las que SÍ se pueden marcar: las activas del puesto que la persona no desempeña ya.
    marcables: function() {
      const funciones = this.funcionesPuesto ? this.funcionesPuesto.funciones : [];
      return funciones.filter(function(f) { return !f.yaVigente; });
    },
    yaVigentes: function() {
      const funciones = this.funcionesPuesto ? this.funcionesPuesto.funciones : [];
      return funciones.filter(function(f) { return !!f.yaVigente; });
    },
    marcadasCount: function() {
      const marcadas = this.marcadas;
      return this.marcables.filter(function(f) { return !!marcadas[f.codigoInterno]; }).length;
    },
    todasMarcadas: function() {
      return this.marcables.length > 0 && this.marcadasCount === this.marcables.length;
    },
    // El estado «a medias» del check de arriba: hay algunas marcadas, pero no todas. No es una
    // clase CSS, es una propiedad del elemento, así que se escribe a mano (ver `pintarCheckTodas`).
    algunasMarcadas: function() {
      return this.marcadasCount > 0 && this.marcadasCount < this.marcables.length;
    },
    textoConfirmar: function() {
      return this.marcadasCount === 1
        ? this.$t("empleadofunciones.anadirUna")
        : this.$t("empleadofunciones.anadirN", { n: this.marcadasCount });
    },
    /**
     * Por qué no se puede confirmar, o null si sí se puede. Lo pinta la ventana <b>junto al
     * botón</b>: un botón deshabilitado y mudo hace que quien lo pulsa crea que la aplicación no
     * responde (§9 del CLAUDE.md del orquestador).
     *
     * El orden va de lo más concreto a lo más general: quien todavía no ha elegido puesto no
     * necesita que le hablen del motivo.
     */
    motivoNoConfirmar: function() {
      if (!this.funcionesPuesto && !this.cargandoPuesto && !this.errorPuesto) {
        return this.$t("empleadofunciones.noHayPuesto");
      }
      if (this.cargandoPuesto) {
        return this.$t("empleadofunciones.cargandoFunciones");
      }
      if (this.errorPuesto) {
        return this.errorPuesto;
      }
      if (!this.funcionesPuesto.funciones.length) {
        return this.$t("empleadofunciones.puestoSinFunciones");
      }
      if (!this.marcables.length) {
        return this.$t("empleadofunciones.yaLasTieneTodas", {
          puesto: this.funcionesPuesto.puestoNombre || this.funcionesPuesto.puestoCodigo || ""
        });
      }
      // Lo que falta, enumerado entero: los tres campos son obligatorios —así están rotulados— y
      // nombrar solo el primero manda a pulsar otra vez para encontrarse el siguiente aviso.
      // El «Desde» entra aquí porque el botón de limpieza del datepicker lo deja vacío: sin esto
      // el botón seguía activo y el lote se incorporaba entero con la fecha de hoy, que el
      // servidor pone por defecto cuando no llega ninguna (revisión CODEX del PR #1789). O sea
      // que no era un error tardío: eran N funciones fechadas por su cuenta y sin decirlo.
      const falta = [];
      if (!this.marcadasCount) {
        falta.push(this.$t("empleadofunciones.faltaItemMarcar"));
      }
      if (!this.desdeLote) {
        falta.push(this.$t("empleadofunciones.faltaItemDesde"));
      }
      if (!String(this.motivoLote || "").trim()) {
        falta.push(this.$t("empleadofunciones.faltaItemMotivo"));
      }
      if (falta.length) {
        const y = this.$t("empleadofunciones.faltaY");
        const que = falta.length === 1 ? falta[0]
          : falta.slice(0, -1).join(", ") + y + falta[falta.length - 1];
        return this.$t("empleadofunciones.faltaLote", { que: que });
      }
      return null;
    }
  },
  watch: {
    username: function() {
      // Las funciones de la persona anterior las tira el carril al pedir las de esta (#472, ver
      // `created`). Lo que queda aquí es el estado de la carga anterior y las ventanas abiertas,
      // que no son lo pintado y hay que retirar aunque aquella carga no llegara a pintar nada.
      this.cargaFallida = false;
      // Y el 403 era de la persona anterior: dejándolo puesto, un fallo pasajero de la nueva
      // pintaba «No autorizado» —que manda sobre `cargaFallida`— y escondía el enlace de
      // reintentar, sin que nada volviera a borrarlo (revisión CODEX del PR #1732).
      this.error403 = false;
      this.finalizando = null;
      // La ventana del puesto apunta a la persona anterior: su «ya la desempeña» y lo marcado son
      // de ella, y confirmar incorporaría las funciones bajo el nombre de la nueva.
      this.cerrarPuesto();
      this.rehacerAlta();
      this.cargar();
    },
    // El estado «a medias» del check de «Marcar / desmarcar todas» no es un atributo ni una clase:
    // es una propiedad del elemento, así que Vue no lo pinta solo.
    algunasMarcadas: function() {
      this.pintarCheckTodas();
    },
    todasMarcadas: function() {
      this.pintarCheckTodas();
    }
  },
  created: function() {
    // Un carril: cambiar de persona lanza otra petición y la vieja puede llegar después, así que
    // solo contesta la última (ver carrilPeticiones). Fuera de `data` porque no es estado que se
    // pinte.
    //
    // #472 · Y es el carril quien tira lo de la persona anterior al pedir la de otra, en vez del
    // `watch`: dejarla puesta mientras carga la nueva deja a la vista funciones ajenas, y
    // «Finalizar» las cerraría bajo el nombre de la nueva.
    const self = this;
    this.carril = carrilPeticiones({
      vaciar: function() {
        self.ficha = null;
        self.permisoEdicion = false;
      }
    });
    // Si el alta que está en pantalla ya se ha empezado a rellenar. Fuera de `data` por lo mismo
    // que el carril: no se pinta, solo decide si se puede tocar el «Desde» por defecto.
    this.altaEmpezada = false;
    // Testigo de la petición de funciones del puesto: elegir otro mientras la anterior está en
    // vuelo tiene que dejar en pantalla el que se acaba de elegir. Fuera de `data` por lo mismo
    // que el carril: no se pinta.
    this.peticionPuesto = 0;
    // Qué puesto se ha pedido ya, para no repetir la consulta con cada una de las dos o tres
    // llamadas que `r-input` hace por selección (ver `alElegirPuesto`). `undefined` y no `null`:
    // `null` es el «— Elige un puesto —», que también tiene que entrar la primera vez.
    this.puestoPedido = undefined;
    this.cargar();
  },
  methods: {
    es403: function(e) {
      const estado = (e && (e.status || (e.xhr && e.xhr.status))) || 0;
      return estado == 403;
    },
    /**
     * Un 403 al escribir dice que el permiso con el que se pintaron los botones ya no vale: el
     * check se ha movido a otra cuenta, o esta ficha ha dejado de poderse editar. Mismo criterio
     * que la carga fallida: se suelta lo que no se puede sostener y se vuelve a preguntar. Sin
     * esto el único aviso era el diálogo del error y la tarjeta seguía con sus botones puestos,
     * invitando a repetir una escritura que el servidor ya ha rechazado.
     */
    revisarPermiso: function(e) {
      if (!this.es403(e)) return;
      this.permisoEdicion = false;
      // Si el permiso con el que se pintó el botón ya no vale, su ventana tampoco: dejarla abierta
      // sería dejar a la vista un punto de entrada a algo que el servidor ya rechaza (§9).
      this.cerrarPuesto();
      this.cargar();
    },
    // Rehace el bloque de alta: sube la clave —que recrea el r-form— y vuelve a calcular su fecha
    // por defecto, que es hoy en el momento de rehacerlo y no cuando se abrió la ficha.
    rehacerAlta: function() {
      this.claveAlta++;
      this.altaEnBlanco = enBlancoAlta();
      // El alta siguiente vuelve a estar por empezar, así que su «Desde» se volverá a mirar.
      this.altaEmpezada = false;
    },
    /**
     * Se entra en el bloque «Añadir función»: es el momento de cada alta en el que todavía no hay
     * nada escrito y ya se sabe qué día es.
     *
     * <p>`rehacerAlta()` solo corre después de guardar un alta o al cambiar de persona, así que en
     * una ficha que se quedó abierta de un día para otro la primera alta posterior seguía
     * proponiendo la fecha de ayer, y aceptar el valor por defecto registraba el tramo con el día
     * equivocado. El popup de finalizar ya calculaba la suya al abrirse (`abrirFinalizar`); esto es
     * lo mismo para el alta (revisión CODEX del PR #1732).</p>
     *
     * <p>Se hace una sola vez por alta —`altaEmpezada`—: volver a ponerlo a hoy cada vez que el
     * foco pasa por el bloque borraría la fecha que se acabara de elegir. Y se escribe en el
     * formulario que ya está montado en vez de rehacerlo, porque rehacerlo bajo el foco cerraría
     * el desplegable o el calendario que se estaba abriendo.</p>
     */
    alEmpezarAlta: function() {
      if (this.altaEmpezada) return;
      this.altaEmpezada = true;
      const hoy = new Date();
      if (this.aIso(this.altaEnBlanco.desde) === this.aIso(hoy)) return;
      this.altaEnBlanco.desde = hoy;
      const form = this.$refs.formAlta;
      // La clave existe desde que se montó el formulario, así que la asignación es reactiva y el
      // campo se repinta con la fecha nueva.
      if (form && form.formData) form.formData.desde = hoy;
    },
    // La fecha viaja al backend como aaaa-mm-dd, que es como la guarda y como la ordena. El
    // datepicker de F7 devuelve un array de Date; el resto de la tarjeta lee cadenas.
    aIso: function(valor) {
      const fecha = Array.isArray(valor) ? valor[0] : valor;
      if (!fecha) return null;
      if (typeof fecha === "string") return fecha.substring(0, 10);
      const mes = ("0" + (fecha.getMonth() + 1)).slice(-2);
      const dia = ("0" + fecha.getDate()).slice(-2);
      return fecha.getFullYear() + "-" + mes + "-" + dia;
    },
    // Lo que se enseña: dd/mm/aaaa. Sin Date de por medio, que con una fecha sin hora se corre un
    // día según la zona horaria del navegador.
    fecha: function(iso) {
      if (!iso) return "—";
      const partes = String(iso).substring(0, 10).split("-");
      return partes.length === 3 ? partes[2] + "/" + partes[1] + "/" + partes[0] : iso;
    },
    // La llama la ficha de empleado tras guardarla, por si el username ha cambiado.
    recargar: function() {
      this.cargar();
    },
    cargar: function() {
      const self = this;
      if (!self.username) return;
      self.carril.lanzar(RhEmpleadoFunciones.get(self.username), {
        // De quién es: con otra persona pintada el carril vacía (#472, ver `created`).
        clave: [self.username],
        exito: function(res) {
          self.error403 = false;
          self.cargaFallida = false;
          self.ficha = self.normalizar(res);
          self.permisoEdicion = res && res.editable === true;
        },
        limpiar: function() {
          // La carga ha fallado, así que lo que hay en pantalla es de ANTES y ya no se sostiene:
          // se suelta entero —la ficha y el permiso que venía con ella— y queda el estado de
          // fallo, que es el único que ofrece reintentar.
          //
          // Antes esto era `self.cargaFallida = !self.ficha`, y con una ficha ya cargada no se
          // veía el fallo. La recarga que sigue a guardar «Vacaciones» viene justo detrás de lo
          // que puede haber movido el check «Gestiona vacaciones en este usuario» a otra cuenta,
          // y con él las funciones vigentes y el permiso para editarlas: si esa recarga falla, la
          // tarjeta se quedaba indefinidamente con las funciones y los botones de la cuenta de
          // antes, sin error y sin «Reintentar» (revisión CODEX del PR #1732). El servidor
          // rechazaría la escritura, pero para entonces ya se ha pulsado el botón: lo que no se ha
          // podido confirmar no se pinta como válido.
          self.ficha = null;
          self.permisoEdicion = false;
          self.cargaFallida = true;
          // El popup de finalizar apunta a un tramo de esa ficha: cerrarlo es parte de soltarla.
          self.finalizando = null;
          // Y la ventana del puesto, por lo mismo: su desplegable sale de la ficha que se acaba
          // de soltar, y lo marcado se decidió contra unas vigentes que ya no se sostienen.
          self.cerrarPuesto();
        },
        fallo: function(e) {
          if (self.es403(e)) {
            self.error403 = true;
            self.ficha = null;
            // Aquí no se ofrece reintentar: el permiso no cambia por volver a pedirlo.
            self.cargaFallida = false;
            return;
          }
          // Un fallo que NO es de permisos: si venía un 403 de antes hay que quitarlo, o la
          // plantilla sigue pintando «No autorizado» —que va primero— y esconde el reintento.
          self.error403 = false;
          self.$f7.dialog.alert(e && e.message ? e.message : self.$t("empleadofunciones.errorCarga"));
        }
      });
    },
    // Las tres listas siempre presentes: la plantilla las recorre y las cuenta, y una respuesta a
    // la que le falte una dejaría la tarjeta en blanco con un error de consola.
    normalizar: function(res) {
      const ficha = res || {};
      return {
        username: ficha.username,
        nombre: ficha.nombre,
        cuentaFunciones: ficha.cuentaFunciones,
        // Por qué la tarjeta está en sólo lectura, redactado por el servidor: el motivo depende de
        // cuál es la cuenta gestora, que es un dato que sólo tiene él.
        motivoSoloLectura: ficha.motivoSoloLectura || null,
        vigentes: ficha.vigentes || [],
        historico: ficha.historico || [],
        funcionesDisponibles: ficha.funcionesDisponibles || [],
        // Como las otras: la plantilla la recorre, y una respuesta sin ella dejaría la ventana en
        // blanco con un error de consola.
        puestosDisponibles: ficha.puestosDisponibles || []
      };
    },
    anadir: function() {
      const self = this;
      const app = self.$f7;
      // El bloque de alta no se pinta cuando no se puede editar, pero es el único camino que
      // escribe: que no dependa de que el bloque siga escondido.
      if (!self.editable) return;
      const datos = self.$refs.formAlta ? self.$refs.formAlta.formData : {};
      if (!datos.codigoFuncion) {
        app.dialog.alert(self.$t("empleadofunciones.faltaFuncion"));
        return;
      }
      // El backend lo rechaza igual; avisar aquí ahorra el viaje y explica por qué.
      if (!datos.motivo || !String(datos.motivo).trim()) {
        app.dialog.alert(self.$t("empleadofunciones.faltaMotivoAlta"));
        return;
      }
      const cuerpo = {
        codigoFuncion: datos.codigoFuncion,
        desde: self.aIso(datos.desde),
        principal: !!datos.principal,
        motivo: String(datos.motivo).trim()
      };
      // El POST puede llegar tarde: si mientras iba se ha cambiado de persona, lo guardado es de
      // la anterior y pintarlo pondría sus funciones bajo el nombre de la nueva.
      const usuario = self.username;
      // #663 · La espera de la sesión: si la sesión cambia en vuelo, la frontera calla el `then` que la apagaba.
      const espera = preloaderDeLaSesion(self);
      espera.mostrar();
      RhEmpleadoFunciones.anadir(usuario, cuerpo)
        .then(function(res) {
          if (usuario !== self.username) return;
          self.aplicar(res);
          // El bloque se vacía rehaciéndolo: dejarlo con lo que se acaba de dar de alta invita a
          // pulsar «Añadir» otra vez y chocar con el «ya desempeña esa función».
          self.rehacerAlta();
          self.avisar(self.$t("empleadofunciones.anadida"));
        })
        .catch(function(e) {
          // Aquí no se mira la persona: el error solo avisa de que no se guardó, y callarlo por
          // haber cambiado de ficha perdería el aviso. El 409 de la segunda función principal
          // llega con el nombre de la que ya la tiene, así que se enseña tal cual.
          if (usuario !== self.username) return;
          app.dialog.alert(e && e.message ? e.message : self.$t("empleadofunciones.errorAnadir"));
          self.revisarPermiso(e);
        })
        .then(function() {
          espera.cerrar();
        });
    },
    // La fecha de fin se propone hoy, que es lo normal; el motivo se escribe siempre. El r-form
    // del popup va con `:key` por el id del tramo, así que abrir otro lo rehace vacío. La fecha se
    // calcula AQUÍ, al abrirlo: con la ficha abierta de un día para otro, la de cuando se montó la
    // tarjeta cerraría la función el día anterior.
    abrirFinalizar: function(fila) {
      this.finVacio = { hasta: new Date(), motivo: null };
      this.finalizando = fila;
    },
    finalizar: function() {
      const self = this;
      const app = self.$f7;
      if (!self.editable || !self.finalizando) return;
      const datos = self.$refs.formFin ? self.$refs.formFin.formData : {};
      const hasta = self.aIso(datos.hasta);
      if (!hasta) {
        app.dialog.alert(self.$t("empleadofunciones.faltaHasta"));
        return;
      }
      if (!datos.motivo || !String(datos.motivo).trim()) {
        app.dialog.alert(self.$t("empleadofunciones.faltaMotivoBaja"));
        return;
      }
      const usuario = self.username;
      const id = self.finalizando.id;
      const espera = preloaderDeLaSesion(self);
      espera.mostrar();
      RhEmpleadoFunciones.finalizar(usuario, id, { hasta: hasta, motivo: String(datos.motivo).trim() })
        .then(function(res) {
          if (usuario !== self.username) return;
          self.aplicar(res);
          self.finalizando = null;
          self.avisar(self.$t("empleadofunciones.finalizada"));
        })
        .catch(function(e) {
          if (usuario !== self.username) return;
          app.dialog.alert(e && e.message ? e.message : self.$t("empleadofunciones.errorFinalizar"));
          self.revisarPermiso(e);
        })
        .then(function() {
          espera.cerrar();
        });
    },
    // ------------------------------------------- «Añadir funciones de un puesto…» (#485)

    /**
     * Abre la ventana en blanco. El «Desde» se calcula AQUÍ, al abrirla, y no al montar la
     * tarjeta: con la ficha abierta de un día para otro incorporaría las funciones con la fecha
     * de ayer, que es el mismo fallo que ya se corrigió en el bloque de alta y en «Finalizar».
     */
    abrirPuesto: function() {
      // El botón no se pinta cuando no se puede editar, pero este es un camino que escribe: que
      // no dependa de que el botón siga escondido.
      if (!this.editable) return;
      this.claveLote++;
      this.loteEnBlanco = enBlancoLote();
      this.funcionesPuesto = null;
      this.cargandoPuesto = false;
      this.errorPuesto = null;
      this.marcadas = {};
      this.motivoLote = "";
      // La ventana se abre con el desplegable en blanco, así que lo pedido la vez anterior no
      // puede frenar la primera selección de esta.
      this.puestoPedido = undefined;
      // El «Desde» que propone el formulario en blanco: el gate tiene que medir lo que se ve en el
      // campo, no un vacío que obligaría a tocar la fecha propuesta para poder confirmar.
      this.desdeLote = this.aIso(this.loteEnBlanco.desde);
      this.popupPuesto = true;
    },
    cerrarPuesto: function() {
      this.popupPuesto = false;
      // La respuesta que venga tarde ya no es de nadie: se invalida con el testigo, igual que en
      // `alElegirPuesto`.
      this.peticionPuesto = (this.peticionPuesto || 0) + 1;
      this.funcionesPuesto = null;
      this.cargandoPuesto = false;
      this.errorPuesto = null;
      this.marcadas = {};
      this.motivoLote = "";
      this.desdeLote = null;
      this.puestoPedido = undefined;
    },
    /**
     * Se ha elegido un puesto: se piden sus funciones activas y se marcan todas de entrada, menos
     * las que la persona ya desempeña, que no se pueden marcar.
     *
     * <p>Quién ya desempeña qué lo decide el servidor y no se cruza aquí con `ficha.vigentes`: la
     * pregunta es sobre la PERSONA —el grupo de usuarios vinculados por «Misma persona que»— y esa
     * resolución vive en el servidor. Rehacerla en el navegador sería escribir por segunda vez la
     * regla que después va a aplicar la escritura.</p>
     *
     * <p>Se pregunta <b>una sola vez por puesto</b>. Una única selección del desplegable llega
     * aquí dos o tres veces —la misma raíz que el motivo «[object Event]» que arregló la ronda
     * anterior: `r-input` pasa sus oyentes al `f7-list-input` de dentro (`v-on="$listeners"`,
     * `rInput.vue:121`) y además su manejador interno llama a `setValue`, que emite otro
     * `change`—. Leer del formulario basta para que el valor sea el bueno, pero no para que no se
     * salga a la red tres veces: el testigo tiraba las respuestas de sobra, y con eso bastaba que
     * la ÚLTIMA fallara para enseñar «no se han podido cargar» teniendo ya la lista buena de la
     * primera (revisión CODEX del PR #1789). El reintento después de un fallo sigue existiendo: se
     * vuelve al «— Elige un puesto —» y se elige otra vez, que es lo único que el desplegable
     * permite cuando ya enseña ese puesto.</p>
     */
    alElegirPuesto: function() {
      const self = this;
      const datos = self.$refs.formLote ? self.$refs.formLote.formData : {};
      const puestoId = datos.puestoId || null;
      if (puestoId === self.puestoPedido) return;
      self.puestoPedido = puestoId;
      // Un testigo por petición: elegir otro puesto mientras la anterior está en vuelo tiene que
      // dejar en pantalla el que se acaba de elegir, no el que conteste más tarde.
      const testigo = (self.peticionPuesto || 0) + 1;
      self.peticionPuesto = testigo;
      self.funcionesPuesto = null;
      self.marcadas = {};
      self.errorPuesto = null;
      if (!puestoId) {
        self.cargandoPuesto = false;
        return;
      }
      // #663 · Cerrojo de la sesión: si la sesión cambia en vuelo, la frontera calla la continuación que lo soltaba.
      cerrojoDeLaSesion(self, 'cargandoPuesto');
      const usuario = self.username;
      RhEmpleadoFunciones.funcionesDePuesto(usuario, puestoId)
        .then(function(res) {
          if (testigo !== self.peticionPuesto || usuario !== self.username) return;
          self.funcionesPuesto = {
            puestoId: res ? res.puestoId : puestoId,
            puestoCodigo: res ? res.puestoCodigo : null,
            puestoNombre: res ? res.puestoNombre : null,
            funciones: (res && res.funciones) || []
          };
          // Todas marcadas de entrada, que es lo que pidió dirección: lo normal es incorporarlas
          // todas y quitar alguna, no al revés.
          const marcadas = {};
          self.funcionesPuesto.funciones.forEach(function(f) {
            if (!f.yaVigente) marcadas[f.codigoInterno] = true;
          });
          self.marcadas = marcadas;
        })
        .catch(function(e) {
          if (testigo !== self.peticionPuesto || usuario !== self.username) return;
          self.errorPuesto = (e && e.message) || self.$t("empleadofunciones.errorFuncionesPuesto");
          self.revisarPermiso(e);
        })
        .then(function() {
          if (testigo !== self.peticionPuesto) return;
          self.cargandoPuesto = false;
        });
    },
    /**
     * Refleja «Desde» y «Motivo» del formulario de la ventana, que es de donde depende el botón de
     * confirmar.
     *
     * <p>Los lee SIEMPRE de `formData` y <b>nunca del argumento del `@change`</b>, que es lo que
     * hacía antes: `r-input` pasa sus oyentes al `f7-list-input` de dentro
     * (`v-on="$listeners"`, `rInput.vue:121`), así que al manejador le llegan <b>dos</b> llamadas
     * por cada cambio —la del `$emit` propio del componente, con el valor, y la del evento nativo
     * del DOM al salir del campo—, y gana la última. `String(evento)` es `"[object Event]"`: el
     * botón se habilitaba y ese literal se guardaba como motivo en el historial de todas las
     * funciones del lote (revisión CODEX del PR #1789). Leyendo del formulario da igual cuántas
     * veces y con qué llamen, que es por lo que `alElegirPuesto` nunca tuvo el problema.</p>
     */
    reflejarLote: function() {
      const datos = this.$refs.formLote ? this.$refs.formLote.formData : {};
      this.motivoLote = datos.motivo == null ? "" : String(datos.motivo);
      this.desdeLote = this.aIso(datos.desde);
    },
    alternar: function(funcion) {
      if (funcion.yaVigente) return;
      // `$set` porque `marcadas` es un objeto plano y una clave nueva no sería reactiva.
      this.$set(this.marcadas, funcion.codigoInterno, !this.marcadas[funcion.codigoInterno]);
    },
    alternarTodas: function() {
      // El criterio es el del check que se ve: si está entero marcado, se desmarca todo; si no
      // —vacío o a medias—, se marca todo lo marcable.
      const marcar = !this.todasMarcadas;
      const marcadas = {};
      this.marcables.forEach(function(f) {
        marcadas[f.codigoInterno] = marcar;
      });
      this.marcadas = marcadas;
    },
    pintarCheckTodas: function() {
      const self = this;
      self.$nextTick(function() {
        const check = self.$refs.checkTodas;
        if (check) check.indeterminate = self.algunasMarcadas;
      });
    },
    confirmarPuesto: function() {
      const self = this;
      const app = self.$f7;
      // Mismo criterio que `anadir`: la ventana no se pinta cuando no se puede editar, pero este
      // es el camino que escribe.
      if (!self.editable || self.motivoNoConfirmar) return;
      // Lo que se envía sale del formulario, igual que lo que mide el gate: `motivoLote` y
      // `desdeLote` son su reflejo (`reflejarLote`) y están aquí de respaldo por si algún camino
      // no hubiera pasado por el `@change`.
      const datos = self.$refs.formLote ? self.$refs.formLote.formData : {};
      const desde = self.aIso(datos.desde) || self.desdeLote;
      const motivo = String((datos.motivo == null ? self.motivoLote : datos.motivo) || "").trim();
      const marcadas = self.marcadas;
      // El código INTERNO, que es con el que el tramo nombra la función desde la #483: el
      // visible se puede reordenar y mandarlo dejaría el alta apuntando a otra función o a
      // ninguna. Es el mismo valor que manda el desplegable del alta de una en una.
      const codigos = self.marcables
        .filter(function(f) { return !!marcadas[f.codigoInterno]; })
        .map(function(f) { return f.codigoInterno; });
      const cuerpo = {
        puestoId: self.funcionesPuesto.puestoId,
        desde: desde,
        motivo: motivo,
        codigosFuncion: codigos
      };
      const usuario = self.username;
      const cuantas = codigos.length;
      const espera = preloaderDeLaSesion(self);
      espera.mostrar();
      RhEmpleadoFunciones.anadirDePuesto(usuario, cuerpo)
        .then(function(res) {
          if (usuario !== self.username) return;
          self.aplicar(res);
          self.cerrarPuesto();
          self.avisar(cuantas === 1
            ? self.$t("empleadofunciones.anadidaUna")
            : self.$t("empleadofunciones.anadidasN", { n: cuantas }));
        })
        .catch(function(e) {
          // La ventana NO se cierra: la incorporación es todo o nada, así que no se ha añadido
          // ninguna y lo marcado sigue valiendo para reintentarlo tras arreglar lo que diga el
          // error.
          if (usuario !== self.username) return;
          app.dialog.alert(e && e.message ? e.message : self.$t("empleadofunciones.errorAnadirPuesto"));
          self.revisarPermiso(e);
        })
        .then(function() {
          espera.cerrar();
        });
    },

    // Lo que acaba de devolver la escritura manda sobre cualquier carga que siga en vuelo.
    aplicar: function(res) {
      this.carril.invalidar();
      this.cargaFallida = false;
      this.ficha = this.normalizar(res);
    },
    avisar: function(texto) {
      this.$f7.toast
        .create({
          text: texto,
          position: "center",
          closeTimeout: 2000,
          icon: '<i class="f7-icons">checkmark_alt</i>'
        })
        .open();
    }
  }
};
</script>
